import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, tap, throwError } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '../../../environments/environment';
import { API_PATHS } from '../../shared/constants/api-paths-constant';

let isLoggingOut = false;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router   = inject(Router);
  const snackBar = inject(MatSnackBar);

  // ─────────────────────────────────────────────
  // 1. Skip interceptor for non-API & auth routes
  // ─────────────────────────────────────────────
  const isApiRequest =
    req.url.includes('dev.bellaspeak.com') ||
    req.url.includes('staging.bellaspeak.com') ||
    req.url.includes('prod-course-lesson.bellaspeak.com') ||
    req.url.includes('studio.bellaspeak.net');

  const isAuthRoute =
    req.url.includes(API_PATHS.loginStudioUser) ||
    req.url.includes(API_PATHS.verifyMFATOTP);

  if (!isApiRequest || isAuthRoute) {
    return next(req);
  }

  // ─────────────────────────────────────────────
  // 2. Pre-request: Validate accessToken exists
  // ─────────────────────────────────────────────
  const accessToken = localStorage.getItem('accessToken');

  if (!accessToken) {
    forceLogout(router, snackBar, 'Session expired. Please login again.');
    return throwError(() => new Error('No access token available'));
  }

  // ─────────────────────────────────────────────
  // 3. Post-response: Check BOTH HTTP errors AND
  //    soft errors inside the response body
  //    e.g. { "status": 403, "message": "MALFORMED_TOKEN" }
  // ─────────────────────────────────────────────
  return next(req).pipe(

    // ✅ Intercept soft auth errors inside HTTP 200 response body
    tap((event: any) => {
      // Only process HttpResponse events that have a body
      if (event?.body) {
        const body = event.body;
        const softStatus  = body?.status;
        const softMessage = body?.message;

        if (environment.enableLogging && softStatus) {
        }

        // ✅ Auth-related soft error codes that require force logout
        const AUTH_FAILURE_MESSAGES = [
          'MALFORMED_TOKEN',
          'TOKEN_EXPIRED',
          'INVALID_TOKEN',
          'UNAUTHORIZED',
          'ACCESS_DENIED',
          'SESSION_EXPIRED',
        ];

        const isAuthFailureStatus  = softStatus === 401 || softStatus === 403;
        const isAuthFailureMessage = softMessage && AUTH_FAILURE_MESSAGES.includes(softMessage);

        if (isAuthFailureStatus || isAuthFailureMessage) {
          const userMessage = getMessageForCode(softMessage);
          forceLogout(router, snackBar, userMessage);
        }
      }
    }),

    // ✅ Intercept real HTTP-level errors (401, 403, 0, 500+)
    catchError((error: HttpErrorResponse) => {
      switch (error.status) {
        case 401:
          forceLogout(router, snackBar, 'Your session has expired. Please login again.');
          break;

        case 403:
          forceLogout(router, snackBar, 'Access denied. You do not have permission.');
          break;

        case 0:
          snackBar.open('Network error. Please check your connection.', 'Close', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
          break;

        default:
          if (error.status >= 500) {
            snackBar.open('Server error. Please try again later.', 'Close', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });
          }
          break;
      }

      return throwError(() => error);
    })
  );
};

// ─────────────────────────────────────────────
// Map backend message codes to user-friendly text
// Add new codes here as your backend evolves
// ─────────────────────────────────────────────
function getMessageForCode(message: string): string {
  const messageMap: { [key: string]: string } = {
    'MALFORMED_TOKEN'  : 'Your session token is invalid. Please login again.',
    'TOKEN_EXPIRED'    : 'Your session has expired. Please login again.',
    'INVALID_TOKEN'    : 'Invalid session detected. Please login again.',
    'UNAUTHORIZED'     : 'Unauthorized access. Please login again.',
    'ACCESS_DENIED'    : 'Access denied. Please login again.',
    'SESSION_EXPIRED'  : 'Your session has expired. Please login again.',
  };
  return messageMap[message] || 'Authentication failed. Please login again.';
}

// ─────────────────────────────────────────────
// Force Logout — race condition guard prevents
// multiple simultaneous API failures from
// triggering logout more than once
// ─────────────────────────────────────────────
function forceLogout(router: Router, snackBar: MatSnackBar, message: string): void {
  if (isLoggingOut) {
    return;
  }

  isLoggingOut = true;

  localStorage.clear();
  sessionStorage.clear();

  snackBar.open(message, 'Close', {
    duration: 6000,
    horizontalPosition: 'center',
    verticalPosition: 'top',
    panelClass: ['error-snackbar']
  });

  router.navigate(['/login']).then(() => {
    setTimeout(() => { isLoggingOut = false; }, 3000);
  });
}
